import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import predibaseSquare from '@/assets/v2/images/predibase-square.png';
import texture from '@/assets/v2/decorators/texture.png';
import Button from '@/components/v2/global/Button';
import { Link } from 'gatsby';
import { SquarePredibase } from '@/components/v2/decorators/SquarePredibase';
import { Decorator, GradientLine, VerticalLine } from '@/components/v2/home/ModelSupportSection/components';

interface ModelSupportSectionProps {
  title: string;
  description: string | readonly {
    readonly type: 'logo' | 'text';
    readonly src?: string;
    readonly alt?: string;
    readonly content?: string;
  }[];
  titleAndDescriptionClassName?: string;
  logos?: readonly string[];
  link?: {
    title: string;
    href: string;
  };
}

const sliderSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 3000,
  slidesToShow: 5,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 0,
  cssEase: 'linear',
  pauseOnHover: false,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
      },
    },
  ],
} as const;

export const ModelSupportSection = ({ title, description, titleAndDescriptionClassName, logos = [], link }: ModelSupportSectionProps) => {
  const hasLogos = logos.length > 0;

  const renderDescription = () => {
    if (typeof description === 'string') {
      return description;
    }
    
    return description.map((item, index) => {
      if (item.type === 'logo' && item.src) {
        return (
          <img
            key={index}
            src={item.src}
            alt={item.alt || ''}
            className="inline-block h-[1.7rem] mx-2"
          />
        );
      }
      if (item.type === 'text' && item.content) {
        return <span key={index}>{item.content}</span>;
      }
      return null;
    });
  };

  return (
    <section className="py-24 bg-[#0E0C0C] relative overflow-hidden">
      <div className="absolute top-0 left-0">
        <Decorator />
      </div>
      <div className="absolute top-0 right-0">
        <Decorator />
      </div>
      <div className="absolute w-full bottom-0 z-0 mix-blend-color-dodge">
        <img src={texture} alt="Texture" className="w-full h-full" />
      </div>
      <div className={`container mx-auto px-2 md:px-4 text-center ${titleAndDescriptionClassName}`}>
        <h2 className="text-2xl lg:text-5xl font-medium text-white">{title}</h2>
        <div className="flex flex-col items-center justify-center mt-8 mb-16">
          <GradientLine />
          <div className="flex">
            <VerticalLine />
            <p className="my-2 text-base text-white max-w-4xl mx-auto flex items-center justify-center flex-wrap">
              {renderDescription()}
            </p>
            <VerticalLine />
          </div>
          <GradientLine />
        </div>
        {hasLogos && (
          <div className="flex flex-col mb-24">
            <GradientLine />
            <div className="flex flex-wrap gap-8 items-center justify-center relative">
              <div className="w-full px-2 lg:px-12 relative">
                <div className="relative before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-[20px] lg:before:w-[200px] before:bg-gradient-to-r before:from-[#0E0C0C] before:to-transparent after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-[20px] lg:after:w-[200px] after:bg-gradient-to-l after:from-[#0E0C0C] after:to-transparent">
                  <Slider {...sliderSettings}>
                    {logos.map((logo, index) => (
                      <div
                        key={index}
                        className="text-gray-400 capitalize px-4 py-3"
                      >
                        <img src={logo} alt="" className="w-auto h-9" loading="lazy" />
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32">
                <SquarePredibase />
                <img src={predibaseSquare} alt="Predibase Square" loading="lazy" />
              </div>
            </div>
            <GradientLine />
          </div>
        )}
        {link && (
          <div className="flex justify-center">
            <Button variant="dark" as={Link} to={link.href} className="text-orange-500 py-[1rem] px-[1.5rem]" target="_blank">
              {link.title}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
